import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { message, Modal } from "antd";

import { generateRandomString } from "utils/helper";
import * as services from "../services/employeeApi";
import { setEducationList, setIsLoading } from "../slices/employeeSlice";

import useOptions from "features/masterData/hooks/useOptions";
import { useTranslation } from "react-i18next";


function useEmployeeEducation() {
  const { i18n, t } = useTranslation();
  const dispatch = useDispatch();
  const params = useParams();
  const [isModalOpen, setModalOpen] = useState(false);
  const [dataDetail, setDataDetail] = useState(undefined);
  const { educationDegreeOptions = [], getEducationDegreeOptions } = useOptions();
  const isLoading = useSelector((state) => state.employee.isLoading);
  const currentCompany = useSelector((state) => state.company.currentCompany);
  let { id } = useParams();
  let employeeId = id;
  const educationList = useSelector((state) => state.employee.educationList);
  const currentEmployee = useSelector(
    (state) => state.employee.currentEmployee
  );

  const [isEditing, setIsEditing] = useState(false);
  // const { t } = useTranslation();
  const toInitialValues = (initialValues = {}) => {
    return initialValues;
  };

  const toParams = (values = {}) => {
    const modifiedValues = {
      ...values,
    };

    return modifiedValues;
  };


  const initialValues = useMemo(() => {
    return toInitialValues(currentEmployee);
  }, [currentEmployee]);

  const onEdit = (values) => {
    setIsEditing(true);
    setModalOpen(true);
    setDataDetail(values);

  };

  const onCreate = useCallback(() => {
    setModalOpen(true);
    setDataDetail(undefined);
  }, []);

  const onCancel = useCallback(() => {
    setIsEditing(false);

  }, []);

  const onSubmit = useCallback(
    (values) => {
      Modal.confirm({
        title: t('dialog.confirmation.header'),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to {values?.id ? "edit" : "create"} ?</span>
          : <span>คุณแน่ใจหรือไม่ที่จะ {values?.id ? "แก้ไข" : "สร้างใหม่"} ?</span>
        ),
        onOk() {
          updateEducationInformation(values);
        },
        okText: t('confirm'),
        cancelText: t('generalInfo.cancel'),
      });
    },
    []
  );

  const onDelete = useCallback(
    async (params = {}) => {
      Modal.confirm({
        title: t("dialog.confirmation.header"),
        content: (i18n.language === "en"
          ? <span>Are you sure confirm to delete ?</span>
          : <span>คุณยืนยันการลบใช่หรือไม่ ?</span>
        ),
        okText: t("common.confirm"),
        cancelText: t("common.cancel"),
        onOk: async () => {
          try {
            dispatch(setIsLoading(true));
            const response = await services.deleteEducationInformation({
              employeeId: id,
              actionId: currentEmployee,
              educationId: params?.id,

            });
            getEducationInformation(
              id
            );
            setEducationList({});
            setModalOpen(false);
            message.success(t("common.success"))
          } catch (error) {
            message.error(t("common.error"))
          } finally {
            dispatch(setIsLoading(false));
          }
        }
      })
    },
    [dispatch, id, params, currentEmployee]
  );


  const updateEducationInformation = useCallback(
    async (values) => {
      try {
        dispatch(setIsLoading(true));
        await services.updateEducationInformation(
          toParams({
            ...values,
            requestId: generateRandomString(),
            companyId: currentCompany,
            employeeId: employeeId,
            educationId: values?.id
          })
        );
        message.success(t("common.success"))
        setIsEditing(false);
        getEducationInformation(
          id
        );
        setEducationList({});
        setModalOpen(false);
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch]
  );

  const getEducationInformation = useCallback(
    async (employeeId) => {
      try {
        dispatch(setIsLoading(true));
        const response = await services.getEducationInformation({
          requestId: generateRandomString(),
          companyId: currentCompany,
          employeeId,
        });
        const updatedEducationList = (response.data?.education || []).map((item) => ({
          ...item,
          educationDegreeName: i18n.language === "en" ? item.educationDegree.nameEn : item.educationDegree.name
        }));
        dispatch(setEducationList(updatedEducationList));
      } catch (error) {
        message.error(t("common.error"))
      } finally {
        dispatch(setIsLoading(false));
      }
    },
    [currentCompany, dispatch, i18n.language]
  );

  useEffect(() => {
    if (params.id && currentCompany) getEducationInformation(params.id);
  }, [currentCompany, params.id, getEducationInformation]);

  useEffect(() => {
    getEducationDegreeOptions();
  }, []);

  return {
    isLoading,
    isModalOpen,
    initialValues,
    educationList,
    educationDegreeOptions,
    dataDetail,
    onEdit,
    onCancel,
    onCreate,
    onDelete,
    onSubmit,
    setModalOpen,
    updateEducationInformation,
  };
}

export default useEmployeeEducation;
